import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, signal, Signal, WritableSignal, AfterViewInit, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureStore } from '@app/_stores';
import { EntityName } from '@app/_types/generic.type';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MatTabsModule } from '@angular/material/tabs';
import { SettingsService } from '@app/_services/settings.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { TodoTrashTabComponent } from '../todos/todo-trash-tab/todo-trash-tab.component';
import { ListTrashTabComponent } from '../lists/list-trash-tab/list-trash-tab.component';
import { NoteTrashTabComponent } from '../notes/note-trash-tab/note-trash-tab.component';
import { HabitTrashTabComponent } from '../habits/habit-trash-tab/habit-trash-tab.component';
import { JournalTrashTabComponent } from '../journals/journal-trash-tab/journal-trash-tab.component';
import { MoneyTrackerTrashTabComponent } from '../money-tracker/money-tracker-trash-tab/money-tracker-trash-tab.component';
import { FocusTrashTabComponent } from '../focus-tracker/focus-trash-tab/focus-trash-tab.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { CacheService } from '@app/_services/cache.service';

@Component({
    selector: 'app-trash',
    imports: [
        CommonModule,
        MatTabsModule,
        SvgComponent,
        NotificationComponent,
        TodoTrashTabComponent,
        ListTrashTabComponent,
        NoteTrashTabComponent,
        HabitTrashTabComponent,
        JournalTrashTabComponent,
        MoneyTrackerTrashTabComponent,
        FocusTrashTabComponent,
        NoResultBlockComponent
    ],
    templateUrl: './trash.component.html',
    styleUrl: './trash.component.scss'
})

export class TrashComponent implements AfterViewInit, OnInit, OnDestroy {

  readonly featureStore = inject(FeatureStore);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  activeEntities: Signal<EntityName[]> = computed(() => {
    return this.featureStore.activeDeletedEntities().filter(entity => entity !== 'calendarIntegration');
  });
  activeTab: WritableSignal<EntityName> = signal(this.featureStore.activeDeletedEntities().filter(entity => entity !== 'calendarIntegration')[0] || 'todo');
  activeEntity: Signal<EntityName> = computed(() => this.featureStore.activeDeletedEntities().length === 1 ? this.featureStore.activeDeletedEntities()[0] : this.activeTab());
  config: {
    [key: string]: {
      name: string, icon: string,
    }
  } = {
      todo: {
        name: this.ss.t('trash_todoTitle'),
        icon: 'todo',
      },
      list: {
        name: this.ss.t('trash_listTitle'),
        icon: 'list',
      },
      note: {
        name: this.ss.t('trash_noteTitle'),
        icon: 'note',
      },
      habit: {
        name: this.ss.t('trash_habitTitle'),
        icon: 'habit',
      },
      journal: {
        name: this.ss.t('trash_journalTitle'),
        icon: 'journal',
      },
      moneyTracker: {
        name: this.ss.t('trash_moneyTrackerTitle'),
        icon: 'moneyTracker',
      },
      focus: {
        name: this.ss.t('common_focusTracker'),
        icon: 'focusTrainer',
      }
    }
    
  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();

  constructor(
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private router: Router,
    public cc: CacheService
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  ngAfterViewInit() {
    this.injectWarningMessage();
  }

  injectWarningMessage() {
    setTimeout(() => {
      const matTabHeader = this.elementRef.nativeElement.querySelector('.mat-mdc-tab-header');
      if (matTabHeader) {
        const existingWarning = this.elementRef.nativeElement.querySelector('.sticky-warning');
        if (existingWarning) {
          this.renderer.removeChild(existingWarning.parentNode, existingWarning);
        }

        const warningP = this.renderer.createElement('p');
        this.renderer.addClass(warningP, 'text-14-400');
        this.renderer.addClass(warningP, 'color-12');
        this.renderer.addClass(warningP, 'mb-0');
        this.renderer.addClass(warningP, 'ri-py-2');
        this.renderer.addClass(warningP, 'ri-px-4');
        this.renderer.addClass(warningP, 'text-center');
        this.renderer.addClass(warningP, 'bg-26');
        this.renderer.addClass(warningP, 'sticky-warning');

        const text = this.renderer.createText(this.ss.t('trash_itemEraseContent'));
        this.renderer.appendChild(warningP, text);

        const parentElement = matTabHeader.parentNode;
        const nextSibling = matTabHeader.nextSibling;
        this.renderer.insertBefore(parentElement, warningP, nextSibling);
      }
    }, 0);
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('drawerTrash', previousScreen);
      }
    });
  }

  setTab(tabIndex: number) {
    this.activeTab.set(this.activeEntities()[tabIndex]);
    this.injectWarningMessage();
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerTrash', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
